import React from 'react';
import {
	BaasicInput,
	Loader,
	BaasicCheckbox,
	BaasicHeader,
	BaasicButton,
	BaasicDropdown,
	TimePicker,
	DatePicker,
	SliderCheckBox,
} from 'core/components';
import { defaultTemplate } from 'core/hoc';
import { channelType, getAmazonOAuthUrl } from 'common/constants';
import { observer } from 'mobx-react';
import { ButtonAndLink } from 'common/utils';
import ReactTooltip from 'react-tooltip';
import _ from 'lodash';

const ChannelEditTemplate = ({
	t,
	channelEditViewStore,
	channelEditViewStore: {
		form,
		loading,
		rootStore,
		isEdit,
		openEditRestockDateModal,
		openEditChannelWarehouseCodesModal,
		openUpdateInventoryLogModal,
		validateCredentials,
		scheduledRestockDate,
		openCustomLabelModal,
		openEditChannelAmazonTemplatesModal,
		channelTypeDropdownStore,
	},
}) => {
	if (loading) return <Loader />;
	const pageTitle = !isEdit ? 'CREATECHANNEL' : 'EDITCHANNEL';

	return (
		<section>
			<BaasicHeader title={pageTitle} rootStore={rootStore}>
				{scheduledRestockDate && (
					<i
						data-tip={t('RESTOCKDATE.SCHEDULEDRESTOCKDATENOTE', { scheduledDate: scheduledRestockDate })}
						className="spc--right--sml far fa-clock type--color--success--light type--med"
					/>
				)}
				<ReactTooltip />
				{/* Restock Date */}
				{isEdit && _.get(channelTypeDropdownStore, 'value.label') === channelType.amazon && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative"
						icon="icon icon--xmed icon--inventory-update spc--right--sml"
						onClick={() => {
							openEditRestockDateModal();
						}}
						label="RESTOCKDATE.TITLE"
					/>
				)}
				{/* Update Inventory */}
				{isEdit && _.get(channelTypeDropdownStore, 'value.label') !== channelType.manualOrders && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--inventory-update spc--right--sml"
						onClick={() => {
							openUpdateInventoryLogModal();
						}}
						label="UPDATEINVENTORYLOG.TITLE"
					/>
				)}
				{/* Warehouse Codes */}
				<BaasicButton
					className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
					icon="icon icon--xmed icon--warehouse-code--white spc--right--sml"
					onClick={() => {
						openEditChannelWarehouseCodesModal();
					}}
					label="WAREHOUSECODES.EDIT"
				/>
				{/* Amazon Templates */}
				{_.get(channelTypeDropdownStore, 'value.label') == channelType.amazon && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--amazon--white spc--right--sml"
						onClick={() => {
							openEditChannelAmazonTemplatesModal();
						}}
						label="AMAZONTEMPLATES.EDIT"
					/>
				)}

				{/* Test Connection */}
				{_.get(channelTypeDropdownStore, 'value.label') !== channelType.manualOrders && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--test-connection--white spc--right--sml"
						onClick={() => {
							validateCredentials();
						}}
						label="CHANNEL.TESTCONNECTION"
					/>
				)}
				{
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--custom-label--white spc--right--sml"
						onClick={() => {
							openCustomLabelModal();
						}}
						label="CHANNEL.CUSTOMIZELABEL"
					/>
				}
			</BaasicHeader>
			<form className="container container--lrg container--footer" onSubmit={form.onSubmit}>
				<div className="container__group__wrapper">
					<GeneralChannelSettings viewStore={channelEditViewStore} t={t} />
					{<ChannelSettings viewStore={channelEditViewStore} t={t} />}

					<div className="container__group">
						{_.get(channelTypeDropdownStore, 'value.label') &&
							![channelType.goFlow, channelType.sellerCloud, channelType.manualOrders].includes(
								_.get(channelTypeDropdownStore, 'value.label')
							) && (
								<div className="spc--bottom--sml">
									<BaasicCheckbox field={form.$('shouldAcknowledgeOrders')} />
								</div>
							)}

						{(_.get(channelTypeDropdownStore, 'value.label') === channelType.amazon ||
							_.get(channelTypeDropdownStore, 'value.label') === channelType.walmart) && (
							<div className="spc--bottom--sml">
								<BaasicCheckbox field={form.$('shouldAutoCancel')} />
							</div>
						)}

						{_.get(channelTypeDropdownStore, 'value.label') === channelType.amazon && (
							<div className="spc--bottom--sml">
								<BaasicCheckbox field={form.$('checkBuyShippingSunday')} />
							</div>
						)}
					</div>
				</div>
				<div className="container__footer type--right">
					<button className="btn btn--med btn--success spc--bottom--sml" type="submit" disabled={loading}>
						{loading ? (
							<div className="spinnercont spc--right--sml">
								<div className="spinner"></div>
							</div>
						) : null}
						{t('SAVE')}
					</button>
				</div>
			</form>
		</section>
	);
};

const GoFlowChannelSettings = observer(({ viewStore, t }) => {
	const { form, goFlowStoreDropdownStore, getGoFlowStores } = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('apiKey')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('subdomain')} />
					</div>
				</div>
				{goFlowStoreDropdownStore && (
					<div className="row row--form">
						<div className="col col-sml-12 col-lrg-6">
							<BaasicDropdown dropdownStore={goFlowStoreDropdownStore} />
						</div>
						<div className="col col-sml-12 col-lrg-6">
							<BaasicButton
								className="btn btn--med btn--secondary"
								label={t('CHANNEL.GETSTORES')}
								onClick={getGoFlowStores}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="col col-sml-12 col-lrg-6">
				<BaasicCheckbox field={form.$('checkSplit')} />
			</div>
		</React.Fragment>
	);
});

const SellerCloudChannelSettings = observer(({ viewStore, t }) => {
	const { form, sellerCloudDropdownStore, sellerCloudCompanyDropdownStore, getSellerCloudCompanies } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('teamName')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('userName')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('password')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicDropdown dropdownStore={sellerCloudDropdownStore} />
				</div>
			</div>
			{sellerCloudCompanyDropdownStore && (
				<div className="row row--form">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicDropdown dropdownStore={sellerCloudCompanyDropdownStore} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicButton
							className="btn btn--med btn--secondary"
							label={t('CHANNEL.GETCOMPANIES')}
							onClick={getSellerCloudCompanies}
						/>
					</div>
				</div>
			)}
		</div>
	);
});

const MiraklChannelSettings = ({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('url')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('shopId')} />
				</div>
			</div>
		</div>
	);
};

const GeneralChannelSettings = observer(({ viewStore, t }) => {
	const {
		form,
		sellerDropdownStore,
		isSuperAdmin,
		isEdit,
		channelTypeDropdownStore,
		timezoneDropdownStore,
		carrierChannelDropdownStore,
		datePickerFormat,
		onLastSyncChange,
	} = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					{isSuperAdmin && (
						<BaasicDropdown
							dropdownStore={sellerDropdownStore}
							placeholder={t('PLACEHOLDER.SELLER')}
							className="w--200--px spc--right--sml"
							isDisabled={isEdit}
						/>
					)}
				</div>
			</div>
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput autoFocus field={form.$('name')} />
				</div>
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={channelTypeDropdownStore} isDisabled={isEdit} />
				</div>
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={timezoneDropdownStore} />
				</div>
				{channelTypeDropdownStore.value && channelTypeDropdownStore.value.label !== channelType.amazonVendor && (
					<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
						<BaasicDropdown dropdownStore={carrierChannelDropdownStore} />
					</div>
				)}
				{isEdit && isSuperAdmin && _.get(channelTypeDropdownStore, 'value.label') !== channelType.manualOrders && (
					<React.Fragment>
						<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
							<DatePicker field={form.$('lastSyncDate')} format={datePickerFormat} onChange={onLastSyncChange} />
						</div>
						<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
							<TimePicker field={form.$('lastSyncTime')} />
						</div>
					</React.Fragment>
				)}
			</div>
		</div>
	);
});

const ShopifyChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('url')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('accessToken')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const OverstockCAChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('partnerId')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const LogicbrokerChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('partnerId')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const CostcoChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('partnerId')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const WooCommerceChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('url')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('consumerKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('consumerSecret')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const AmazonVendorChannelSettings = observer(({ viewStore, t }) => {
	const { form, onAuthorizeAmazon, amazonOAuthStateHash } = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<h3 className="container__subtitle">{t('CHANNEL.SELLINGPARTNERAPICREDENTIALS')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('amazonSellerId')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('marketplaceId')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('refreshToken')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('sellingPartyId')} className="fs-mask" />
					</div>
				</div>
				<ButtonAndLink
					label={t('CHANNEL.AUTHORIZEVENDOR')}
					btnClass="btn btn--med btn--primary spc--right--sml spc--bottom--sml"
					url={getAmazonOAuthUrl(true, amazonOAuthStateHash)}
					onClick={() => onAuthorizeAmazon()}
				/>
			</div>

			{process.env.NODE_ENV !== 'production' && (
				<div className="spc--bottom--sml">
					<BaasicCheckbox field={form.$('useSandbox')} />
				</div>
			)}
		</React.Fragment>
	);
});

const AmazonChannelSettings = observer(({ viewStore, t }) => {
	const { form, onAuthorizeAmazon, amazonOAuthStateHash, isEdit, channel, buyShippingDropdowns } = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<h3 className="container__subtitle">{t('CHANNEL.SELLINGPARTNERAPICREDENTIALS')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('amazonSellerId')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('marketplaceId')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('refreshToken')} className="fs-mask" />
					</div>
				</div>

				{
					<ButtonAndLink
						label={t('CHANNEL.AUTHORIZESELLER')}
						btnClass="btn btn--med btn--primary spc--right--sml spc--bottom--med"
						url={getAmazonOAuthUrl(false, amazonOAuthStateHash)}
						onClick={() => onAuthorizeAmazon()}
					/>
				}
				<h3 className="container__subtitle">{t('CHANNEL.BUYSHIPPINGCARRIERS')}</h3>

				{/* Use Buy Shipping toogle/slider */}
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useBuyShippingForPrime')} />
				</div>
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useBuyShippingForNonPrime')} />
				</div>

				{buyShippingDropdowns.map(item => {
					return (
						<React.Fragment id={item.id}>
							<div className="row">
								<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
									<BaasicDropdown
										dropdownStore={item}
										isDisabled={!form.$('useBuyShippingForPrime').value && !form.$('useBuyShippingForNonPrime').value}
										isClearable={true}
									/>
								</div>
							</div>
						</React.Fragment>
					);
				})}

				<h3 className="container__subtitle">{t('CHANNEL.UPDATEINVENTORY')}</h3>
				{isEdit && form.$('useAmazonMli').value == false && form.$('useAmazonMli').value != channel.useAmazonMli && (
					<p className="type--color--note spc--bottom--sml">{t('CHANNEL.MLIRESET')}</p>
				)}
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useAmazonMli')} />
				</div>
			</div>
		</React.Fragment>
	);
});

const WalmartChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiSecret')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const ChannelSettings = observer(({ viewStore, t }) => {
	switch (_.get(viewStore.channelTypeDropdownStore, 'value.label')) {
		case channelType.goFlow:
			return <GoFlowChannelSettings viewStore={viewStore} t={t} />;
		case channelType.amazonVendor:
			return <AmazonVendorChannelSettings viewStore={viewStore} t={t} />;
		case channelType.amazon:
			return <AmazonChannelSettings viewStore={viewStore} t={t} />;
		case channelType.walmart:
			return <WalmartChannelSettings viewStore={viewStore} />;
		case channelType.wooCommerce:
			return <WooCommerceChannelSettings viewStore={viewStore} />;
		case channelType.costco:
			return <CostcoChannelSettings viewStore={viewStore} />;
		case channelType.logicBroker:
			return <LogicbrokerChannelSettings viewStore={viewStore} />;
		case channelType.overstockCA:
			return <OverstockCAChannelSettings viewStore={viewStore} />;
		case channelType.buybuyBaby:
		case channelType.mattressFirm:
		case channelType.homeDepot:
		case channelType.overstock:
		case channelType.wayfair:
			return <LogicbrokerChannelSettings viewStore={viewStore} />;
		case channelType.mirakl:
			return <MiraklChannelSettings viewStore={viewStore} />;
		case channelType.shopify:
			return <ShopifyChannelSettings viewStore={viewStore} />;
		case channelType.sellerCloud:
			return <SellerCloudChannelSettings viewStore={viewStore} t={t} />;
	}
});

export default defaultTemplate(ChannelEditTemplate);



// WEBPACK FOOTER //
// ./src/themes/default/modules/channel/pages/ChannelEditTemplate.jsx